import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getGetConvertResultRevenueAxisRevenueAppropriationsQueryKey,
  useGetConvertResultExpenditureOptions,
  usePatchSettlementStatisticsRevenueAppropriation,
  usePostSettlementStatisticsRevenueAppropriation,
} from '@/entities/settlementstatisticsconverter/api/generated/api';
import {
  type ConvertResultSettlementOption,
  type ExpenditureSummaryForRevenueAxisAppropriationResponse,
  OptionsFieldsParamParameterItem,
  type RevenueRecordForRevenueAxisAppropriationResponse,
} from '@/entities/settlementstatisticsconverter/api/generated/model';
import { buildExpenditureCategoryCascaderOptions } from '@/entities/settlementstatisticsconverter/lib/buildExpenditureCategoryCascaderOptions';
import { buildProjectCategoryCascaderOptions } from '@/entities/settlementstatisticsconverter/lib/buildProjectCategoryCascaderOptions';
import { buildSettlementCascaderOptions } from '@/entities/settlementstatisticsconverter/lib/buildSettlementCascaderOptions';
import { fundingTypeSelectOptions } from '@/entities/settlementstatisticsconverter/lib/funding-type';
import { recurringTypeSelectOptions } from '@/entities/settlementstatisticsconverter/lib/recurring-type';
import { toSelectOptions } from '@/shared/lib/utils';
import {
  Button,
  CascaderField,
  Form,
  InputField,
  SelectField,
  Skeleton,
  toast,
  useSkeletonMinDisplay,
} from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import {
  type RevenueAppropriationExpenditureFormValues,
  revenueAppropriationExpenditureSchema,
} from './schema';

/**
 * カスケーダーのコードパスから決算統計オプションのidを逆引きする
 *
 * level4選択時はカスケーダーのvalueにidを設定しているためそのまま使用。
 * level3以浅がリーフの場合はコードパスから元データを検索してidを取得する。
 */
const findSettlementId = (
  items: ConvertResultSettlementOption[] | undefined | null,
  path: string[],
): string | undefined => {
  if (!items || path.length === 0) {
    return undefined;
  }
  // コードパスから元データを検索してidを取得する
  return (
    items.find(
      (item) =>
        item.level1_code === path[0] &&
        (path.length < 2 || item.level2_code === path[1]) &&
        (path.length < 3 || item.level3_code === path[2]) &&
        (path.length < 4 || item.level4_code === path[3]),
    )?.id ?? undefined
  );
};

type RevenueAppropriationExpenditureFormProps = {
  /** 変換結果ID */
  convertResultId: string;
  /** パネルを閉じる */
  onClose: () => void;
} & (
  | {
      mode: 'create';
      /** 追加対象の歳入レコード */
      revenueRecord: RevenueRecordForRevenueAxisAppropriationResponse;
    }
  | {
      mode: 'edit';
      /** 財源充当レコードID */
      revenueAppropriationId: string;
      /** 編集対象の歳出サマリーデータ */
      expenditureSummary: ExpenditureSummaryForRevenueAxisAppropriationResponse;
    }
);

/**
 * 財源充当（歳出）フォーム
 *
 * 追加モード: 歳入レコードに対して新しい歳出サマリーとの財源充当を追加する。
 * 編集モード: 既存の充当金額のみ変更可能。歳出情報は readOnly で表示。
 */
export const RevenueAppropriationExpenditureForm = (
  props: RevenueAppropriationExpenditureFormProps,
) => {
  const { convertResultId, onClose, mode } = props;
  const isEditMode = mode === 'edit';

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // オプション取得
  const { data: optionsData, isLoading: isOptionsLoading } = useGetConvertResultExpenditureOptions(
    convertResultId,
    {
      fields: [
        OptionsFieldsParamParameterItem.accounting_type,
        OptionsFieldsParamParameterItem.expenditure_purpose,
        OptionsFieldsParamParameterItem.expenditure_project,
        OptionsFieldsParamParameterItem.expenditure_project_category,
        OptionsFieldsParamParameterItem.expenditure_settlement_purpose,
        OptionsFieldsParamParameterItem.expenditure_settlement_nature,
      ],
    },
  );

  // edit モードのデフォルト値を構築
  const editDefaults = (() => {
    if (props.mode !== 'edit') return undefined;
    const s = props.expenditureSummary;
    return {
      accounting_type_code: s.accounting_type_code,
      expenditure_category_code_path: [
        s.expenditure_kan_code,
        s.expenditure_ko_code,
        s.expenditure_moku_code,
      ],
      project_category_code_path: [
        s.project_category1_code,
        s.project_category2_code,
        s.project_category3_code,
      ].filter(Boolean),
      project_code: s.project_code,
      settlement_purpose_code_path: [
        s.settlement_purpose1_code,
        s.settlement_purpose2_code,
        s.settlement_purpose3_code,
        s.settlement_purpose4_code,
      ].filter(Boolean),
      settlement_nature_code_path: [
        s.settlement_nature1_code,
        s.settlement_nature2_code,
        s.settlement_nature3_code,
        s.settlement_nature4_code,
      ].filter(Boolean),
      recurring_type:
        s.recurring_type as RevenueAppropriationExpenditureFormValues['recurring_type'],
      funding_type: s.funding_type as RevenueAppropriationExpenditureFormValues['funding_type'],
      revenue_appropriation_amount: s.total_appropriation_amount ?? 0,
    } satisfies RevenueAppropriationExpenditureFormValues;
  })();

  const form = useForm<RevenueAppropriationExpenditureFormValues>({
    resolver: zodResolver(revenueAppropriationExpenditureSchema),
    defaultValues: editDefaults ?? {
      accounting_type_code: '',
      expenditure_category_code_path: [],
      project_category_code_path: [],
      project_code: '',
      settlement_purpose_code_path: [],
      settlement_nature_code_path: [],
      recurring_type: undefined,
      funding_type: undefined,
      revenue_appropriation_amount: undefined,
    },
  });
  const { control, handleSubmit, setValue, watch, formState } = form;

  const watchAccountingTypeCode = watch('accounting_type_code');

  // 会計区分オプション
  const accountingTypeOptions = toSelectOptions(optionsData?.accounting_types);

  // 歳出科目（款項目）カスケーダー（選択中の会計区分でフィルタ）
  const expenditureCategoryOptions = buildExpenditureCategoryCascaderOptions(
    optionsData?.expenditure_purposes,
    watchAccountingTypeCode || null,
  );

  // 事業階層カスケーダー
  const projectCategoryOptions = buildProjectCategoryCascaderOptions(
    optionsData?.expenditure_project_categories,
  );

  // 事業セレクト
  const projectOptions = optionsData?.expenditure_projects
    ? optionsData.expenditure_projects.map((p) => ({
        value: p.project_code,
        label: `${p.project_code}:${p.project_name}`,
      }))
    : [];

  // 決算統計目的カスケーダー
  const settlementPurposeOptions = buildSettlementCascaderOptions({
    settlements: optionsData?.expenditure_settlement_purposes,
  });

  // 決算統計性質カスケーダー
  const settlementNatureOptions = buildSettlementCascaderOptions({
    settlements: optionsData?.expenditure_settlement_natures,
  });

  const { mutate: postRevenueAppropriation, isPending: isPostPending } =
    usePostSettlementStatisticsRevenueAppropriation({
      request: { suppressToast: true },
    });

  const { mutate: patchRevenueAppropriation, isPending: isPatchPending } =
    usePatchSettlementStatisticsRevenueAppropriation();

  const isPending = isPostPending || isPatchPending;
  const revenueAppropriationName = t('common.data-types.expenditure');

  const onSubmit = (values: RevenueAppropriationExpenditureFormValues) => {
    if (isEditMode) {
      patchRevenueAppropriation(
        {
          convertResultId,
          revenueAppropriationId: props.revenueAppropriationId,
          data: {
            revenue_appropriation_amount: values.revenue_appropriation_amount,
          },
        },
        {
          onSuccess: () => {
            toast.success(t('common.messages.update-success', { name: revenueAppropriationName }));
            queryClient.invalidateQueries({
              queryKey:
                getGetConvertResultRevenueAxisRevenueAppropriationsQueryKey(convertResultId),
            });
            onClose();
          },
          onError: () => {
            toast.error(t('common.messages.update-failed', { name: revenueAppropriationName }));
          },
        },
      );
      return;
    }

    // create モード
    const categoryPath = values.expenditure_category_code_path;
    const kanCode = categoryPath[0] ?? '';
    const koCode = categoryPath[1] ?? '';
    const mokuCode = categoryPath[2] ?? '';

    const projectCategoryPath = values.project_category_code_path ?? [];

    const purposeId = findSettlementId(
      optionsData?.expenditure_settlement_purposes,
      values.settlement_purpose_code_path,
    );
    const natureId = findSettlementId(
      optionsData?.expenditure_settlement_natures,
      values.settlement_nature_code_path,
    );

    postRevenueAppropriation(
      {
        convertResultId,
        data: {
          expenditure_kan_code: kanCode,
          expenditure_ko_code: koCode,
          expenditure_moku_code: mokuCode,
          expenditure_project_category1_code: projectCategoryPath[0] ?? null,
          expenditure_project_category2_code: projectCategoryPath[1] ?? null,
          expenditure_project_category3_code: projectCategoryPath[2] ?? null,
          expenditure_project_code: values.project_code,
          expenditure_purpose_id: purposeId ?? '',
          expenditure_nature_id: natureId ?? '',
          expenditure_recurring_type: values.recurring_type,
          expenditure_funding_type: values.funding_type,
          revenue_id: props.revenueRecord.revenue_record_id,
          expenditure_accounting_type_code: values.accounting_type_code,
          revenue_appropriation_amount: values.revenue_appropriation_amount,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('common.messages.create-success', { name: revenueAppropriationName }));
          queryClient.invalidateQueries({
            queryKey: getGetConvertResultRevenueAxisRevenueAppropriationsQueryKey(convertResultId),
          });
          onClose();
        },
        onError: (e) => {
          if (e.response?.data.error_code === 'NOT_FOUND') {
            toast.error(
              t(
                'settlement-statistics-converter.messages.revenue-appropriation-invalid-expenditure',
              ),
            );
            return;
          }
          if (e.response?.data.error_code === 'VALIDATION_ERROR') {
            toast.error(
              t(
                'settlement-statistics-converter.messages.revenue-appropriation-duplicate-expenditure',
              ),
            );
            return;
          }
          toast.error(t('common.messages.create-failed', { name: revenueAppropriationName }));
        },
      },
    );
  };

  const showSkeleton = useSkeletonMinDisplay(isOptionsLoading);
  if (showSkeleton) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: formState.dirtyFields,
          isSubmitting: formState.isSubmitting,
          isSubmitSuccessful: formState.isSubmitSuccessful,
        }}
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* スクロール可能なフォーム領域 */}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
          <SelectField
            control={control}
            name="accounting_type_code"
            label={t('common.fields.accounting-type')}
            options={accountingTypeOptions}
            onValueChange={() => {
              setValue('expenditure_category_code_path', []);
            }}
            required
            readOnly={isEditMode}
          />

          <CascaderField
            control={control}
            name="expenditure_category_code_path"
            label={t('common.fields.expenditure-category')}
            options={expenditureCategoryOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            required
            disabled={!watchAccountingTypeCode}
            readOnly={isEditMode}
          />

          <CascaderField
            control={control}
            name="project_category_code_path"
            label={t('settlement-statistics-converter.common-fields.project-category')}
            options={projectCategoryOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            readOnly={isEditMode}
          />

          <SelectField
            control={control}
            name="project_code"
            label={t('common.fields.project')}
            options={projectOptions}
            showSearch
            required
            readOnly={isEditMode}
          />

          <CascaderField
            control={control}
            name="settlement_purpose_code_path"
            label={t('settlement-statistics-converter.common-fields.settlement-purpose')}
            options={settlementPurposeOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            required
            readOnly={isEditMode}
          />

          <CascaderField
            control={control}
            name="settlement_nature_code_path"
            label={t('settlement-statistics-converter.common-fields.settlement-nature')}
            options={settlementNatureOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            required
            readOnly={isEditMode}
          />

          <SelectField
            control={control}
            name="recurring_type"
            label={t('common.fields.recurring-type')}
            options={recurringTypeSelectOptions}
            required
            readOnly={isEditMode}
          />

          <SelectField
            control={control}
            name="funding_type"
            label={t('common.csv-headers.funding-type')}
            options={fundingTypeSelectOptions}
            required
            readOnly={isEditMode}
          />

          <InputField
            control={control}
            name="revenue_appropriation_amount"
            label={t(
              'settlement-statistics-converter.revenue-appropriation.revenue-appropriation-amount',
            )}
            min={0}
            useCommaFormat
            suffix={t('common.unit.yen')}
            type="number"
            autoComplete={false}
            required
          />
        </div>

        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
